import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, LayoutGrid, Tag } from "lucide-react";
import Navigation from "../components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CategoriesPage({ user }) {
  const [categories, setCategories] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/categories`)
      .then((resp) => {
        setCategories(resp.data.categories || []);
        setTotalDocs(resp.data.total_documents || 0);
      })
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#f7f6f2]">
      <Navigation user={user} />
      <main>
        <section className="border-b border-slate-200 bg-slate-950 text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-orange-300 mb-4">
              <LayoutGrid className="w-4 h-4" /> The Archive · Categories
            </div>
            <h1 className="text-4xl sm:text-5xl font-semibold" data-testid="categories-page-title">
              Browse every shelf
            </h1>
            <p className="mt-3 text-slate-300" data-testid="categories-page-count">
              {loading ? "Loading…" : `${categories.length} categories · ${totalDocs.toLocaleString()} documents`}
            </p>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="categories-grid">
            {categories.map((cat) => (
              <Link
                key={cat.category}
                to={`/category/${encodeURIComponent(cat.category)}`}
                className="group bg-white border border-slate-200 rounded-xl shadow-sm p-5 flex items-center justify-between gap-3 hover:border-orange-400 hover:shadow-md transition-all"
                data-testid={`category-card-${cat.category}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {(cat.thumbnails || []).length > 0 ? (
                    <div className="flex -space-x-4 shrink-0 py-1" data-testid={`category-collage-${cat.category}`}>
                      {cat.thumbnails.map((tid, i) => (
                        <img
                          key={tid}
                          src={`${API}/items/${tid}/thumbnail`}
                          alt=""
                          loading="lazy"
                          className="w-10 h-14 object-cover rounded border-2 border-white shadow-sm bg-slate-100"
                          style={{ zIndex: 3 - i, transform: `rotate(${(i - 1) * 5}deg)` }}
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="w-10 h-10 shrink-0 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center group-hover:bg-orange-100 transition-colors">
                      <Tag className="w-5 h-5" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-950 truncate" style={{ fontFamily: "Fraunces, serif" }}>
                      {cat.category}
                    </p>
                    <p className="text-xs text-slate-500">
                      {cat.count.toLocaleString()} document{cat.count === 1 ? "" : "s"}
                    </p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 shrink-0 text-slate-300 group-hover:text-orange-600 group-hover:translate-x-0.5 transition-all" />
              </Link>
            ))}
          </div>
          {!loading && categories.length === 0 && (
            <p className="text-center text-slate-500 py-16">No categories yet.</p>
          )}
        </section>
      </main>
    </div>
  );
}
